import { Component, Input } from '@angular/core';
import { EventsService } from '../../../../../../../../@shared/services/events-service';
import { EventAnnouncementResponse, EventResponse } from '../../../../../../../../@shared/models/response.module';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DeleteModal } from '../../../../../../../../@shared/components/delete-modal/delete-modal';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../../../../../@core/services/toast.service';

@Component({
  selector: 'app-manage-event-announcements',
  standalone: false,
  templateUrl: './manage-event-announcements.html',
  styleUrl: './manage-event-announcements.css'
})
export class ManageEventAnnouncements {
  @Input() event: EventResponse;

  announcements: EventAnnouncementResponse[] = [];
  loading = true;
  error: string | null = null;

  // Form state
  showForm = false;
  editingAnnouncement: EventAnnouncementResponse | null = null;
  formTitle = '';
  formDescription = '';
  saving = false;

  // Image upload
  selectedImages: File[] = [];
  uploadingImages = false;

  constructor(
    private eventService: EventsService,
    private modalService: NgbModal,
    private translateService: TranslateService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.loadAnnouncements();
  }

  loadAnnouncements() {
    this.loading = true;
    this.eventService.getAnnouncements({ event_id: this.event.event_id }).subscribe({
      next: (data) => {
        this.announcements = data || [];
        this.loading = false;
      },
      error: () => {
        this.error = this.translateService.instant('manageEvent.announcements.loadError');
        this.loading = false;
      }
    });
  }

  openCreateForm() {
    this.showForm = true;
    this.editingAnnouncement = null;
    this.formTitle = '';
    this.formDescription = '';
    this.selectedImages = [];
  }

  openEditForm(announcement: EventAnnouncementResponse) {
    this.showForm = true;
    this.editingAnnouncement = announcement;
    this.formTitle = announcement.title;
    this.formDescription = announcement.description || '';
    this.selectedImages = [];
  }

  cancelForm() {
    this.showForm = false;
    this.editingAnnouncement = null;
    this.formTitle = '';
    this.formDescription = '';
    this.selectedImages = [];
  }

  saveAnnouncement() {
    if (!this.formTitle.trim()) return;

    this.saving = true;

    if (this.editingAnnouncement) {
      // Update existing
      this.eventService.updateAnnouncement({
        announcement_id: this.editingAnnouncement.announcement_id,
        title: this.formTitle,
        description: this.formDescription
      }).subscribe({
        next: (updated) => {
          const idx = this.announcements.findIndex(a => a.announcement_id === updated.announcement_id);
          if (idx >= 0) {
            this.announcements[idx] = updated;
          }
          this.uploadImagesForAnnouncement(updated.announcement_id);
          this.toastService.success(this.translateService.instant('manageEvent.announcements.updateSuccess'));
        },
        error: (err) => {
          this.saving = false;
          this.toastService.error(err.error?.reason || this.translateService.instant('manageEvent.announcements.updateError'));
        }
      });
    } else {
      // Create new
      this.eventService.createAnnouncement({
        event_id: this.event.event_id,
        title: this.formTitle,
        description: this.formDescription
      }).subscribe({
        next: (created) => {
          this.announcements.unshift(created);
          this.uploadImagesForAnnouncement(created.announcement_id);
          this.toastService.success(this.translateService.instant('manageEvent.announcements.createSuccess'));
        },
        error: (err) => {
          this.saving = false;
          this.toastService.error(err.error?.reason || this.translateService.instant('manageEvent.announcements.createError'));
        }
      });
    }
  }

  async uploadImagesForAnnouncement(announcementId: number) {
    if (this.selectedImages.length === 0) {
      this.finishSave();
      return;
    }

    this.uploadingImages = true;

    for (let i = 0; i < this.selectedImages.length; i++) {
      try {
        const updated = await this.eventService.uploadAnnouncementImage({
          announcement_id: announcementId,
          image: this.selectedImages[i],
          position: i
        }).toPromise();

        if (updated) {
          const idx = this.announcements.findIndex(a => a.announcement_id === announcementId);
          if (idx >= 0) {
            this.announcements[idx] = updated;
          }
        }
      } catch (e) {
        console.error('Error uploading image:', e);
      }
    }

    this.uploadingImages = false;
    this.finishSave();
  }

  finishSave() {
    this.saving = false;
    this.showForm = false;
    this.editingAnnouncement = null;
    this.formTitle = '';
    this.formDescription = '';
    this.selectedImages = [];
  }

  deleteAnnouncement(announcement: EventAnnouncementResponse) {
    const modalRef = this.modalService.open(DeleteModal, { size: 'md', centered: true });
    modalRef.componentInstance.description = this.translateService.instant('manageEvent.announcements.deleteConfirm', { title: announcement.title });
    modalRef.result.then((confirmed) => {
      if (confirmed) {
        this.eventService.deleteAnnouncement({
          announcement_id: announcement.announcement_id
        }).subscribe({
          next: () => {
            this.announcements = this.announcements.filter(a => a.announcement_id !== announcement.announcement_id);
            this.toastService.success(this.translateService.instant('manageEvent.announcements.deleteSuccess'));
          },
          error: (err) => {
            this.toastService.error(err.error?.reason || this.translateService.instant('manageEvent.announcements.deleteError'));
          }
        });
      }
    }).catch(() => {});
  }

  deleteImage(announcement: EventAnnouncementResponse, imageId: number) {
    this.eventService.deleteAnnouncementImage({ image_id: imageId }).subscribe({
      next: () => {
        const idx = this.announcements.findIndex(a => a.announcement_id === announcement.announcement_id);
        if (idx >= 0) {
          this.announcements[idx].images = this.announcements[idx].images.filter(img => img.image_id !== imageId);
        }
        this.toastService.success(this.translateService.instant('manageEvent.announcements.imageDeleteSuccess'));
      },
      error: (err) => {
        this.toastService.error(err.error?.reason || this.translateService.instant('manageEvent.announcements.imageDeleteError'));
      }
    });
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.selectedImages = [...this.selectedImages, ...Array.from(input.files)];
    }
  }

  removeSelectedImage(index: number) {
    this.selectedImages.splice(index, 1);
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
